import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material';
import { EmailOutlined, LockOutlined, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { apiService } from '../services/api';

const PasswordResetPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => String(searchParams.get('token') || '').trim(), [searchParams]);
  const initialEmail = useMemo(() => String(searchParams.get('email') || '').trim(), [searchParams]);

  const [requestEmail, setRequestEmail] = useState(initialEmail);
  const [resetEmail, setResetEmail] = useState(initialEmail);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const hasResetToken = Boolean(token);

  const handleRequestReset = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await apiService.post('/api/auth/password-reset/request', { email: requestEmail });
      setMessage(response?.message || 'Falls ein Konto existiert, wurde eine E-Mail versendet.');
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Der Reset-Link konnte nicht angefordert werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein.');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.post('/api/auth/password-reset/confirm', {
        email: resetEmail,
        token,
        password: newPassword,
      });

      setMessage(response?.message || 'Passwort wurde zurückgesetzt.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (resetError) {
      setError(resetError?.response?.data?.message || 'Das Passwort konnte nicht zurückgesetzt werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f2942 0%, #1a365d 50%, #2d3748 100%)', py: 6 }}>
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 24px 80px rgba(15, 41, 66, 0.35)' }}>
          <Box sx={{ p: 4, background: 'linear-gradient(135deg, #1a365d 0%, #0f2942 100%)', color: '#fff' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Passwort zurücksetzen
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {hasResetToken
                ? 'Bitte wählen Sie ein neues Passwort für Ihr Konto.'
                : 'Fordern Sie einen sicheren Link zum Zurücksetzen an.'}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}
            {message ? <Alert severity="success" sx={{ mb: 3 }}>{message}</Alert> : null}

            {hasResetToken ? (
              <Box component="form" onSubmit={handleResetPassword}>
                <TextField
                  fullWidth
                  label="E-Mail-Adresse"
                  value={resetEmail}
                  onChange={(event) => setResetEmail(event.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Neues Passwort"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((value) => !value)} edge="end">
                          {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Passwort bestätigen"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword((value) => !value)} edge="end">
                          {showConfirmPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, py: 1.5, bgcolor: '#1a365d', '&:hover': { bgcolor: '#0f2942' } }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Passwort speichern'}
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleRequestReset}>
                <TextField
                  fullWidth
                  label="E-Mail-Adresse"
                  value={requestEmail}
                  onChange={(event) => setRequestEmail(event.target.value)}
                  margin="normal"
                  type="email"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, py: 1.5, bgcolor: '#1a365d', '&:hover': { bgcolor: '#0f2942' } }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset-Link anfordern'}
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Link component={RouterLink} to="/login" underline="hover" sx={{ color: '#1a365d' }}>
                Zurück zum Login
              </Link>
              <Button onClick={() => navigate('/register')} sx={{ color: '#1a365d' }}>
                Neu registrieren
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PasswordResetPage;